import type { ReactNode, CSSProperties } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { HolderOutlined } from "@ant-design/icons";

const baseRowStyle = (
  selected?: boolean,
  isOver?: boolean,
  preview?: boolean
): CSSProperties => {
  let borderColor = "#f0f0f0";
  let background = "#fff";
  let boxShadow = "none";
  if (isOver) {
    // 커서 바로 아래(드롭 예정) = 진한 파랑
    borderColor = "#1677ff";
    background = "#e6f4ff";
    boxShadow = "0 0 0 2px rgba(22,119,255,0.2)";
  } else if (preview) {
    // 이동 대상 미리보기 = 주황
    borderColor = "#ffa940";
    background = "#fff7e6";
  } else if (selected) {
    // 현재 위치 = 파랑
    borderColor = "#91caff";
    background = "#f0f7ff";
  }
  return {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 10px",
    marginBottom: 6,
    borderRadius: 6,
    border: `1px solid ${borderColor}`,
    background,
    boxShadow,
    cursor: "pointer",
    userSelect: "none",
    transition: "background 0.15s, border-color 0.15s",
  };
};

interface SortableRowProps {
  dndIdStr: string;
  label: string;
  selected?: boolean;
  preview?: boolean;
  onClick?: () => void;
  right?: ReactNode;
}

/**
 * 정렬(드래그) + 드롭 타깃 + 선택(클릭) 가능한 행.
 * 드래그는 좌측 핸들에서만 시작되어, 행 클릭(선택)과 충돌하지 않는다.
 */
export const SortableRow = ({ dndIdStr, label, selected, preview, onClick, right }: SortableRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } =
    useSortable({ id: dndIdStr });

  const style: CSSProperties = {
    ...baseRowStyle(selected, isOver && !isDragging, preview),
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} onClick={onClick}>
      <span
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        style={{ cursor: "grab", color: "#bfbfbf", display: "flex", touchAction: "none" }}
        aria-label="drag handle"
      >
        <HolderOutlined />
      </span>
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", userSelect: "text" }}>
        {label}
      </span>
      {right}
    </div>
  );
};

interface DroppableRowProps {
  dndIdStr: string;
  label: string;
  selected?: boolean;
  preview?: boolean;
  onClick?: () => void;
  right?: ReactNode;
}

/**
 * 드래그 소스는 아니지만 드롭 타깃 + 선택(클릭)이 되는 행. (게시판 컬럼용)
 */
export const DroppableRow = ({ dndIdStr, label, selected, preview, onClick, right }: DroppableRowProps) => {
  const { setNodeRef, isOver } = useDroppable({ id: dndIdStr });
  return (
    <div ref={setNodeRef} style={baseRowStyle(selected, isOver, preview)} onClick={onClick}>
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", userSelect: "text" }}>
        {label}
      </span>
      {right}
    </div>
  );
};

/**
 * DragOverlay 로 커서를 따라다니는 미리보기 행.
 */
export const OverlayRow = ({ label }: { label: string }) => (
  <div
    style={{
      ...baseRowStyle(false, false),
      boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
      cursor: "grabbing",
      background: "#fff",
      maxWidth: 320,
    }}
  >
    <HolderOutlined style={{ color: "#bfbfbf" }} />
    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
      {label}
    </span>
  </div>
);
